// ---------------------------------------------------------------------------
// Carousel Renderer (editor key: Carousel)
// ---------------------------------------------------------------------------
// Editor's Carousel block is a horizontal-scroll image strip with snap. We
// publish the same layout using native CSS scroll-snap so it works without
// JavaScript — pure markup + inline style. Each slide is a list item with an
// img; alt text comes from the operator, blank alt is allowed for decorative
// slides. Height is clamped to a sane range to keep inline style safe.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, imageUrl } from "../escape.js";

interface RawSlide {
  src?: unknown;
  alt?: unknown;
}

interface CarouselProps {
  images?: RawSlide[] | unknown;
  height?: number | string;
}

interface Slide {
  src: string;
  alt: string;
}

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 1200;
const DEFAULT_HEIGHT = 400;

function clampHeight(value: unknown): number {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_HEIGHT;
  if (numeric < MIN_HEIGHT) return MIN_HEIGHT;
  if (numeric > MAX_HEIGHT) return MAX_HEIGHT;
  return Math.round(numeric);
}

function parseSlides(raw: unknown): Slide[] {
  if (!Array.isArray(raw)) return [];
  const out: Slide[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const slide = item as RawSlide;
    const src = typeof slide.src === "string" ? slide.src.trim() : "";
    if (!src) continue;
    const alt = typeof slide.alt === "string" ? slide.alt : "";
    out.push({ src, alt });
  }
  return out;
}

function renderSlide(slide: Slide, index: number): string {
  return `      <li class="ease-carousel__slide" style="flex:0 0 100%;scroll-snap-align:start">
        <img src="${imageUrl(slide.src)}" alt="${attr(slide.alt)}" loading="${index === 0 ? "eager" : "lazy"}" style="display:block;width:100%;height:100%;object-fit:cover" />
      </li>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as CarouselProps;

  const slides = parseSlides(p.images);
  if (slides.length === 0) {
    return "<!-- carousel missing slides -->";
  }

  const height = clampHeight(p.height);
  const items = slides.map(renderSlide).join("\n");

  return `<section class="ease-section ease-carousel" aria-roledescription="carousel">
  <div class="ease-carousel__viewport" style="position:relative;overflow:hidden;border-radius:12px;height:${attr(String(height))}px">
    <ol class="ease-carousel__track" role="list" style="display:flex;overflow-x:auto;scroll-snap-type:x mandatory;height:100%;margin:0;padding:0;list-style:none">
${items}
    </ol>
  </div>
</section>`;
}

registerRenderer("carousel", { render });
